"use client";

import { c as _c } from "react/compiler-runtime";
import { IconInfoCircle, IconMarkdown } from "@tabler/icons-react";
import { createContext, useContext, useMemo, useState, useTransition } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/toast";
import { useSession } from "@/lib/auth-client";
import { createComment, updateComment } from "@/lib/server/comments";
import { CommentAvatar } from "./comment-avatar";

// Context for lifting form state to parent components
type CommentFormContextValue = {
  content: string;
  setContent: (value: string) => void;
  isPending: boolean;
  startTransition: React.TransitionStartFunction;
};
const CommentFormContext = createContext<CommentFormContextValue | null>(null);

// Provider for sharing form state with sibling components (preview, character counter, etc.)
const CommentFormProvider = t0 => {
  const $ = _c(6);
  const {
    children,
    initialContent: t1
  } = t0;
  const initialContent = t1 === undefined ? "" : t1;
  const [content, setContent] = useState(initialContent);
  const [isPending, startTransition] = useTransition();
  let t2;
  if ($[0] !== content || $[1] !== isPending) {
    t2 = {
      content,
      setContent,
      isPending,
      startTransition
    };
    $[0] = content;
    $[1] = isPending;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const contextValue = t2;
  let t3;
  if ($[3] !== children || $[4] !== contextValue) {
    t3 = <CommentFormContext.Provider value={contextValue}>{children}</CommentFormContext.Provider>;
    $[3] = children;
    $[4] = contextValue;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
};

// Hook to access form state from context (for sibling components like preview panels)
const useCommentForm = () => {
  const context = useContext(CommentFormContext);
  if (!context) {
    throw new Error("useCommentForm must be used within a CommentFormProvider");
  }
  return context;
};

// Internal hook - uses context if available, otherwise creates local state
const useCommentFormState = t0 => {
  const $ = _c(3);
  const initialContent = t0 === undefined ? "" : t0;
  const context = useContext(CommentFormContext);
  const [localContent, setLocalContent] = useState(initialContent);
  const [localIsPending, localStartTransition] = useTransition();
  if (context) {
    return context;
  }
  let t1;
  if ($[0] !== localContent || $[1] !== localIsPending) {
    t1 = {
      content: localContent,
      setContent: setLocalContent,
      isPending: localIsPending,
      startTransition: localStartTransition
    };
    $[0] = localContent;
    $[1] = localIsPending;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};

// Shared textarea component
const CommentTextarea = t0 => {
  const $ = _c(8);
  const {
    content,
    setContent,
    isPending,
    placeholder,
    ariaLabel
  } = t0;
  let t1;
  if ($[0] !== setContent) {
    t1 = e => setContent(e.target.value);
    $[0] = setContent;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== ariaLabel || $[3] !== content || $[4] !== isPending || $[5] !== placeholder || $[6] !== t1) {
    t2 = <Textarea value={content} onChange={t1} placeholder={placeholder} aria-label={ariaLabel} className="min-h-[4lh] w-full" disabled={isPending} />;
    $[2] = ariaLabel;
    $[3] = content;
    $[4] = isPending;
    $[5] = placeholder;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};

// Current user's avatar (uses session)
const CurrentUserAvatar = () => {
  const $ = _c(3);
  const {
    data: session
  } = useSession();
  if (!session?.user) {
    return null;
  }
  let t0;
  if ($[0] !== session.user.image || $[1] !== session.user.name) {
    t0 = <div className="shrink-0"><CommentAvatar name={session.user.name} image={session.user.image} /></div>;
    $[0] = session.user.image;
    $[1] = session.user.name;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};

// Submit button with pending state
const SubmitButton = t0 => {
  const $ = _c(7);
  const {
    isPending,
    disabled,
    pendingLabel,
    children
  } = t0;
  const t1 = isPending || disabled;
  let t2;
  if ($[0] !== children || $[1] !== isPending || $[2] !== pendingLabel) {
    t2 = isPending ? <><Spinner />{pendingLabel}</> : children;
    $[0] = children;
    $[1] = isPending;
    $[2] = pendingLabel;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <Button type="submit" disabled={t1}>{t2}</Button>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};

// Markdown help popover (only shown for new comments)
const MarkdownHelp = () => {
  const $ = _c(10);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <IconMarkdown className="mr-1.5 inline-block size-4 align-text-top" />;
    t1 = <span className="max-md:hidden">Basic </span>;
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <PopoverTrigger render={<button type="button" className="cursor-pointer font-semibold text-primary no-underline decoration-primary/40 decoration-2 underline-offset-4 hover:underline"><span>Markdown</span><span className="max-md:hidden"> syntax</span></button>} />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <p className="text-sm leading-loose"><IconInfoCircle className="mr-1.5 inline size-4.5 align-text-top" />Examples:</p>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <li><span className="font-bold">**bold**</span></li>;
    t5 = <li><span className="italic">_italics_</span></li>;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  let t6;
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <li>[<a href="https://jarv.is" target="_blank" rel="noopener" className="hover:no-underline">links</a>](https://jarv.is)</li>;
    t7 = <li><span className="rounded-sm bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm font-medium">`code`</span></li>;
    $[6] = t6;
    $[7] = t7;
  } else {
    t6 = $[6];
    t7 = $[7];
  }
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <ul className="my-2 list-inside list-disc pl-1 text-sm [&>li]:my-1.5 [&>li]:pl-1 [&>li]:text-nowrap [&>li::marker]:font-normal [&>li::marker]:text-muted-foreground">{t4}{t5}{t6}{t7}<li>~~<span className="line-through">strikethrough</span>~~</li></ul>;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="text-[0.8rem] leading-relaxed text-muted-foreground">{t0}{t1}<Popover>{t2}<PopoverContent align="start">{t3}{t8}<p className="text-sm leading-loose"><a href="https://docs.github.com/en/get-started/writing-on-github/getting-started-with-writing-and-formatting-on-github/basic-writing-and-formatting-syntax" target="_blank" rel="noopener noreferrer">Learn more.</a></p></PopoverContent></Popover><span> is supported</span><span className="max-md:hidden"> here</span><span>.</span></div>;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  return t9;
};

// New comment form - for creating top-level comments
const NewCommentForm = t0 => {
  const $ = _c(22);
  const {
    slug
  } = t0;
  const {
    content,
    setContent,
    isPending,
    startTransition
  } = useCommentFormState();
  let t1;
  if ($[0] !== content || $[1] !== setContent || $[2] !== slug || $[3] !== startTransition) {
    t1 = e => {
      e.preventDefault();
      if (!content.trim()) {
        toast.add({
          title: "Comment cannot be empty.",
          type: "error"
        });
        return;
      }
      startTransition(async () => {
        ;
        try {
          await createComment({
            content,
            pageSlug: slug
          });
          toast.add({
            title: "Comment posted!",
            type: "success"
          });
          setContent("");
        } catch (t2) {
          const error = t2;
          console.error("Error submitting comment:", error);
          toast.add({
            title: "Failed to submit comment. Please try again.",
            type: "error"
          });
        }
      });
    };
    $[0] = content;
    $[1] = setContent;
    $[2] = slug;
    $[3] = startTransition;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const handleSubmit = t1;
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <CurrentUserAvatar />;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== content || $[7] !== isPending || $[8] !== setContent) {
    t3 = <CommentTextarea content={content} setContent={setContent} isPending={isPending} placeholder={"Write your thoughts\u2026"} ariaLabel="Write a comment" />;
    $[6] = content;
    $[7] = isPending;
    $[8] = setContent;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <MarkdownHelp />;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== content) {
    t5 = content.trim();
    $[11] = content;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const t6 = !t5;
  let t7;
  if ($[13] !== isPending || $[14] !== t6) {
    t7 = <div className="flex justify-between gap-4">{t4}<SubmitButton isPending={isPending} disabled={t6} pendingLabel="Posting...">Comment</SubmitButton></div>;
    $[13] = isPending;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t3 || $[17] !== t7) {
    t8 = <div className="flex gap-4">{t2}<div className="min-w-0 flex-1 space-y-4">{t3}{t7}</div></div>;
    $[16] = t3;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== handleSubmit || $[20] !== t8) {
    t9 = <form onSubmit={handleSubmit} className="space-y-4" data-intent="create">{t8}</form>;
    $[19] = handleSubmit;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  return t9;
};

// Reply form - for replying to existing comments
const ReplyForm = ({
  slug,
  parentId,
  onCancel,
  onSuccess
}: {
  slug: string;
  parentId: string;
  onCancel: () => void;
  onSuccess?: () => void;
}) => {
  const {
    content,
    setContent,
    isPending,
    startTransition
  } = useCommentFormState();
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!content.trim()) {
      toast.add({
        title: "Comment cannot be empty.",
        type: "error"
      });
      return;
    }
    startTransition(async () => {
      try {
        await createComment({
          content,
          parentId,
          pageSlug: slug
        });
        toast.add({
          title: "Comment posted!",
          type: "success"
        });
        setContent("");
        onSuccess?.();
      } catch (error) {
        console.error("Error submitting comment:", error);
        toast.add({
          title: "Failed to submit comment. Please try again.",
          type: "error"
        });
      }
    });
  };
  return <form onSubmit={handleSubmit} className="space-y-4" data-intent="create">
      <div className="flex gap-4">
        <CurrentUserAvatar />

        <div className="min-w-0 flex-1 space-y-4">
          <CommentTextarea content={content} setContent={setContent} isPending={isPending} placeholder="Reply to this comment…" ariaLabel="Write a reply" />

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onCancel} disabled={isPending}>
              Cancel
            </Button>

            <SubmitButton isPending={isPending} disabled={!content.trim()} pendingLabel="Posting...">
              Reply
            </SubmitButton>
          </div>
        </div>
      </div>
    </form>;
};

// Edit comment form - for editing existing comments
const EditCommentForm = ({
  slug,
  commentId,
  initialContent,
  onCancel,
  onSuccess
}: {
  slug: string;
  commentId: string;
  initialContent: string;
  onCancel: () => void;
  onSuccess?: () => void;
}) => {
  const {
    content,
    setContent,
    isPending,
    startTransition
  } = useCommentFormState(initialContent);
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!content.trim()) {
      toast.add({
        title: "Comment cannot be empty.",
        type: "error"
      });
      return;
    }
    startTransition(async () => {
      try {
        await updateComment(commentId, content);
        toast.add({
          title: "Comment updated!",
          type: "success"
        });
        onSuccess?.();
      } catch (error) {
        console.error("Error updating comment:", error);
        toast.add({
          title: "Failed to update comment. Please try again.",
          type: "error"
        });
      }
    });
  };
  return <form onSubmit={handleSubmit} className="space-y-4" data-intent="edit" data-slug={slug}>
      <div className="min-w-0 flex-1 space-y-4">
        <CommentTextarea content={content} setContent={setContent} isPending={isPending} placeholder="Edit your comment…" ariaLabel="Edit your comment" />

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onCancel} disabled={isPending}>
            Cancel
          </Button>

          <SubmitButton isPending={isPending} disabled={!content.trim()} pendingLabel="Updating...">
            Edit
          </SubmitButton>
        </div>
      </div>
    </form>;
};
export { NewCommentForm, ReplyForm, EditCommentForm, CommentFormProvider, useCommentForm };